import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Wrench, CheckCircle2, Clock, RotateCcw, CalendarClock, Camera, Search, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same status set + colors as CommandQueue.jsx's getStatusColor, so a
// room's PM history reads consistently with the Command Queue badge.
const STATUS_COLORS = {
  OPEN: "bg-red-500/10 text-red-500 border-red-500/20",
  ACKNOWLEDGED: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  IN_PROGRESS: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  RESOLVED: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  CLOSED: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
};

const liveStatusBadge = (status) => {
  if (!status || !STATUS_COLORS[status]) return null;
  return { label: status, className: STATUS_COLORS[status] };
};

const RoomPM = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [template, setTemplate] = useState({ items: [], total_rooms: 0 });
  const [roomNumber, setRoomNumber] = useState("");
  const [checklistState, setChecklistState] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyRoom, setHistoryRoom] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [expandedApprovalId, setExpandedApprovalId] = useState(null);
  const [baselineSubmission, setBaselineSubmission] = useState(null);
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [baselineChecked, setBaselineChecked] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleRoomNumber, setScheduleRoomNumber] = useState("");
  const [scheduleStart, setScheduleStart] = useState("");
  const [scheduleEnd, setScheduleEnd] = useState("");
  const [creatingSchedule, setCreatingSchedule] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [itemSearch, setItemSearch] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);

  const isHkReview = user?.role === "HOD" && user?.department === "HK";
  const canApprove = ["DM", "GM"].includes(user?.role) || isHkReview;
  const canSubmitChecklist = (user?.role === "HOD" && (user?.department === "ENG" || user?.department === "HK")) || user?.role === "DM"; // TEMPORARY: DM access for briefing demo
  const canViewSchedule = isHkReview || canSubmitChecklist || canApprove;

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/room-pm/overview`);
      setOverview(res.data);
    } catch (err) {
      toast.error("Failed to load RPM overview");
    }
  };

  const openRoomHistory = async (roomNumber) => {
    setHistoryRoom(roomNumber);
    setExpandedHistoryId(null);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/room-pm/rooms/${roomNumber}/history`);
      setHistoryData(res.data);
    } catch (err) {
      toast.error("Failed to load room history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchTemplate = async () => {
    try {
      const res = await axios.get(`${API}/room-pm/checklist-template`);
      setTemplate(res.data);
    } catch (err) {
      toast.error("Failed to load checklist template");
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const res = await axios.get(`${API}/room-pm/submissions?approval_status=PENDING`);
      setPendingApprovals(res.data);
    } catch (err) {
      toast.error("Failed to load approval queue");
    }
  };

  const fetchSchedules = async () => {
    setScheduleLoading(true);
    try {
      const res = await axios.get(`${API}/room-pm/schedules`);
      setSchedules(res.data);
    } catch (err) {
      toast.error("Failed to load RPM schedules");
    } finally {
      setScheduleLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchOverview(),
      fetchTemplate(),
      canApprove ? fetchPendingApprovals() : Promise.resolve(),
      canViewSchedule ? fetchSchedules() : Promise.resolve(),
    ]).finally(() => setLoading(false));
  }, []);

  const handleCreateSchedule = async () => {
    if (!scheduleRoomNumber.trim() || !scheduleStart || !scheduleEnd) {
      toast.error("Room number, start date, and end date are required");
      return;
    }
    setCreatingSchedule(true);
    try {
      await axios.post(`${API}/room-pm/schedules`, {
        room_number: scheduleRoomNumber.trim(),
        start_date: scheduleStart,
        end_date: scheduleEnd,
      });
      toast.success("RPM schedule set");
      setScheduleRoomNumber("");
      setScheduleStart("");
      setScheduleEnd("");
      fetchSchedules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set schedule");
    } finally {
      setCreatingSchedule(false);
    }
  };

  const handleCancelSchedule = async (id) => {
    try {
      await axios.delete(`${API}/room-pm/schedules/${id}`);
      toast.success("Schedule cancelled");
      fetchSchedules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel schedule");
    }
  };

  const setItemStatus = (itemId, status) => {
    setChecklistState((prev) => ({ ...prev, [itemId]: { ...prev[itemId], status } }));
  };

  const setItemNote = (itemId, catatan) => {
    setChecklistState((prev) => ({ ...prev, [itemId]: { ...prev[itemId], catatan } }));
  };

  const handleItemPhotoUpload = async (itemId, file) => {
    if (!file) return;
    setUploadingItemId(itemId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setChecklistState((prev) => ({ ...prev, [itemId]: { ...prev[itemId], foto: res.data.url } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingItemId(null);
    }
  };

  const loadRoomForReview = async () => {
    if (!roomNumber.trim()) {
      toast.error("Room number is required");
      return;
    }
    setBaselineLoading(true);
    setBaselineChecked(false);
    setBaselineSubmission(null);
    setChecklistState({});
    try {
      const res = await axios.get(`${API}/room-pm/rooms/${roomNumber.trim()}/history`);
      const pmApproved = res.data.find(
        (sub) => sub.approval_status === "APPROVED" && sub.checklist_type !== "VERIFICATION"
      );
      if (!pmApproved) {
        toast.error("This room hasn't completed Engineering PM yet this year");
        return;
      }
      // Prefill from the most recent approved submission (PM or a prior HK verification),
      // so HK's own latest findings aren't silently wiped back to Engineering's original baseline.
      const latestApproved = res.data.find((sub) => sub.approval_status === "APPROVED") || pmApproved;
      setBaselineSubmission(pmApproved);
      const prefill = {};
      latestApproved.checklist_items.forEach((i) => {
        prefill[i.id] = { status: i.status, catatan: i.catatan || "" };
      });
      setChecklistState(prefill);
    } catch (err) {
      toast.error("Failed to load room history");
    } finally {
      setBaselineLoading(false);
      setBaselineChecked(true);
    }
  };

  const handleSubmit = async () => {
    if (!roomNumber.trim()) {
      toast.error("Room number is required");
      return;
    }
    if (isHkReview && !baselineSubmission) {
      toast.error("Load the room's Engineering PM checklist first");
      return;
    }
    const items = template.items.map((t) => ({
      id: t.id,
      status: checklistState[t.id]?.status || "OK",
      catatan: checklistState[t.id]?.catatan || null,
      foto: checklistState[t.id]?.foto || null,
    }));
    const belumDiisi = items.filter((i) => !checklistState[i.id]?.status);
    if (belumDiisi.length > 0) {
      toast.error(`${belumDiisi.length} items have no status`);
      return;
    }
    const perluPerbaikanTanpaRemark = items.filter(
      (i) => i.status === "PERLU_PERBAIKAN" && !i.catatan?.trim()
    );
    if (perluPerbaikanTanpaRemark.length > 0) {
      toast.error(`${perluPerbaikanTanpaRemark.length} item(s) marked "Needs Repair" missing description`);
      return;
    }
    const perluPerbaikanTanpaFoto = items.filter((i) => i.status === "PERLU_PERBAIKAN" && !i.foto);
    if (perluPerbaikanTanpaFoto.length > 0) {
      toast.error(`${perluPerbaikanTanpaFoto.length} item(s) marked "Needs Repair" missing photo`);
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/room-pm/submissions`, {
        room_number: roomNumber.trim(),
        checklist_items: items,
      });
      toast.success(isHkReview ? "Verification submitted and completed" : "Checklist submitted for approval");
      setRoomNumber("");
      setChecklistState({});
      setItemSearch("");
      setBaselineSubmission(null);
      setBaselineChecked(false);
      fetchOverview();
      if (canApprove) fetchPendingApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit checklist");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/room-pm/submissions/${id}/approve`);
      toast.success("Submission approved");
      fetchPendingApprovals();
      fetchOverview();
    } catch (err) {
      toast.error("Failed to approve");
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Rejection reason (optional):");
    try {
      await axios.put(`${API}/room-pm/submissions/${id}/reject`, { reject_reason: reason || null });
      toast.success("Submission rejected");
      fetchPendingApprovals();
    } catch (err) {
      toast.error("Failed to reject");
    }
  };

  const categories = [...new Set(template.items.map((i) => i.category))];
  const alreadyFlaggedByEng = new Set(
    (baselineSubmission?.checklist_items || [])
      .filter((i) => i.status === "PERLU_PERBAIKAN")
      .map((i) => i.id)
  );

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6">
        <div>
          <h1 className="font-heading text-2xl uppercase tracking-wider flex items-center gap-2">
            <Wrench className="w-6 h-6 text-orange-500" />
            Room Preventive Maintenance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Golden Tulip Holland Resort Batu</p>
        </div>

        <div className="flex items-center gap-2 border-b border-border">
          <button
            onClick={() => setTab("overview")}
            className={`text-sm px-4 py-2 border-b-2 transition-colors ${tab === "overview" ? "border-orange-500 text-foreground" : "border-transparent text-muted-foreground"}`}
          >
            Overview
          </button>
          {canSubmitChecklist && (
            <button
              onClick={() => setTab("submit")}
              className={`text-sm px-4 py-2 border-b-2 transition-colors ${tab === "submit" ? "border-orange-500 text-foreground" : "border-transparent text-muted-foreground"}`}
            >
              {isHkReview ? "Review Checklist" : "Fill Checklist"}
            </button>
          )}
          {canApprove && (
            <button
              onClick={() => setTab("approvals")}
              className={`text-sm px-4 py-2 border-b-2 transition-colors flex items-center gap-1.5 ${tab === "approvals" ? "border-orange-500 text-foreground" : "border-transparent text-muted-foreground"}`}
            >
              Approval
              {pendingApprovals.length > 0 && (
                <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-[10px]">{pendingApprovals.length}</Badge>
              )}
            </button>
          )}
          {canViewSchedule && (
            <button
              onClick={() => setTab("schedule")}
              className={`text-sm px-4 py-2 border-b-2 transition-colors ${tab === "schedule" ? "border-orange-500 text-foreground" : "border-transparent text-muted-foreground"}`}
            >
              Schedule
            </button>
          )}
        </div>

        {tab === "overview" && overview && (
          <Card className="orbit-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-sm uppercase tracking-wider">
                PM Progress {overview.tahun}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm text-muted-foreground">
                  {overview.done_count} of {overview.total_rooms} rooms
                </span>
                <span className="text-3xl font-bold">{overview.progress_pct}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden mb-4">
                <div
                  className="h-full bg-green-600 rounded-full transition-all"
                  style={{ width: `${overview.progress_pct}%` }}
                />
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                <span className="flex items-center gap-1.5"><CheckCircle2 size={12} className="text-green-500" /> Completed ({overview.done_count})</span>
                <span className="flex items-center gap-1.5"><Clock size={12} className="text-muted-foreground" /> Pending ({overview.pending_count})</span>
                <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-orange-500" /> Open Issues ({overview.rooms_with_open_issues?.length || 0})</span>
                <span className="flex items-center gap-1.5"><RotateCcw size={12} className="text-amber-500" /> Repeat PM ({overview.repeat_count})</span>
              </div>
              {overview.rooms.length > 0 && (
                <div className="grid grid-cols-6 sm:grid-cols-10 gap-1.5">
                  {overview.rooms.map((r) => (
                    <button
                      key={r.room_number}
                      onClick={() => openRoomHistory(r.room_number)}
                      className={`aspect-square rounded flex items-center justify-center text-[9px] text-white cursor-pointer hover:opacity-80 transition-opacity ${overview.rooms_with_open_issues?.includes(r.room_number) ? "bg-orange-500" : "bg-green-600"}`}
                      title={`${r.room_number} - ${r.pm_count}x PM`}
                    >
                      {r.room_number}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === "submit" && (
          <Card className="orbit-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-sm uppercase tracking-wider">
                {isHkReview ? "Review Room After Engineering PM" : "Fill PM Checklist"}
              </CardTitle>
              {isHkReview && (
                <p className="text-xs text-muted-foreground">
                  Loads the room's approved Engineering checklist — flag anything you still find as Needs Repair.
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Room Number</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={roomNumber}
                    onChange={(e) => {
                      setRoomNumber(e.target.value);
                      if (isHkReview) {
                        setBaselineSubmission(null);
                        setBaselineChecked(false);
                        setChecklistState({});
                      }
                    }}
                    placeholder="e.g. 1201"
                    className="w-full sm:w-48 px-3 py-2 text-sm rounded-sm border border-border bg-background"
                  />
                  {isHkReview && (
                    <Button onClick={loadRoomForReview} disabled={baselineLoading} variant="outline">
                      {baselineLoading ? "Loading..." : "Load PM Checklist"}
                    </Button>
                  )}
                </div>
                {isHkReview && baselineChecked && !baselineSubmission && (
                  <p className="text-xs text-red-400 mt-2">
                    This room hasn't completed Engineering PM yet this year — nothing to review.
                  </p>
                )}
                {isHkReview && baselineSubmission && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Loaded PM by {baselineSubmission.submitted_by_name || "-"} on{" "}
                    {new Date(baselineSubmission.submitted_at).toLocaleDateString("en-GB")}.
                  </p>
                )}
              </div>

              {(!isHkReview || baselineSubmission) && (
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    placeholder="Search checklist item..."
                    className="w-full pl-8 pr-8 py-2 text-sm rounded-sm border border-border bg-background"
                  />
                  {itemSearch && (
                    <button
                      onClick={() => setItemSearch("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              )}

              {(!isHkReview || baselineSubmission) && categories.map((cat) => {
                const itemsInCat = template.items.filter(
                  (i) => i.category === cat && i.description.toLowerCase().includes(itemSearch.trim().toLowerCase())
                );
                if (itemsInCat.length === 0) return null;
                return (
                <div key={cat}>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2 mt-3">{cat}</p>
                  <div className="space-y-2">
                    {itemsInCat.map((item) => (
                      <div key={item.id} className="p-2 rounded-sm bg-muted/20">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm flex-1 flex items-center gap-2">
                            {item.description}
                            {isHkReview && alreadyFlaggedByEng.has(item.id) && (
                              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[9px]">Already flagged by ENG</Badge>
                            )}
                          </span>
                          <div className="flex gap-1.5 shrink-0">
                            <button
                              onClick={() => setItemStatus(item.id, "OK")}
                              className={`text-[10px] px-2.5 py-1 rounded-sm transition-colors ${checklistState[item.id]?.status === "OK" ? "bg-green-600 text-white" : "border border-border text-muted-foreground"}`}
                            >
                              OK
                            </button>
                            <button
                              onClick={() => setItemStatus(item.id, "PERLU_PERBAIKAN")}
                              className={`text-[10px] px-2.5 py-1 rounded-sm transition-colors ${checklistState[item.id]?.status === "PERLU_PERBAIKAN" ? "bg-red-600 text-white" : "border border-border text-muted-foreground"}`}
                            >
                              Needs Repair
                            </button>
                          </div>
                        </div>
                        {checklistState[item.id]?.status === "PERLU_PERBAIKAN" && (
                          <>
                            <input
                              type="text"
                              placeholder="Required: describe the issue / why it cannot be fixed on the spot"
                              value={checklistState[item.id]?.catatan || ""}
                              onChange={(e) => setItemNote(item.id, e.target.value)}
                              className="w-full mt-2 px-2.5 py-1.5 text-xs rounded-sm border border-red-400/40 bg-background"
                            />
                            <div className="flex items-center gap-2 mt-2">
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                id={`rpm-photo-${item.id}`}
                                className="hidden"
                                onChange={(e) => handleItemPhotoUpload(item.id, e.target.files?.[0])}
                              />
                              <label
                                htmlFor={`rpm-photo-${item.id}`}
                                className={`text-[10px] px-2.5 py-1.5 rounded-sm border cursor-pointer flex items-center gap-1.5 ${checklistState[item.id]?.foto ? "border-border text-muted-foreground" : "border-red-400/40 text-red-400"}`}
                              >
                                <Camera size={11} />
                                {uploadingItemId === item.id
                                  ? "Uploading..."
                                  : checklistState[item.id]?.foto
                                  ? "Retake Photo"
                                  : "Take Photo (required)"}
                              </label>
                              {checklistState[item.id]?.foto && (
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}${checklistState[item.id].foto}`}
                                  alt="defect evidence"
                                  className="w-10 h-10 object-cover rounded border border-border cursor-pointer hover:opacity-80"
                                  onClick={() => setSelectedImage(`${process.env.REACT_APP_BACKEND_URL}${checklistState[item.id].foto}`)}
                                />
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                );
              })}

              <Button
                onClick={handleSubmit}
                disabled={submitting || uploadingItemId !== null || (isHkReview && !baselineSubmission)}
                className="w-full mt-4"
              >
                {submitting ? "Submitting..." : isHkReview ? "Submit Verification" : "Submit for Approval"}
              </Button>
            </CardContent>
          </Card>
        )}

        {tab === "schedule" && (
          <Card className="orbit-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
                <CalendarClock size={16} className="text-orange-500" /> RPM Schedule
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Engineering can only submit a room's PM checklist within a window Housekeeping schedules here.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {isHkReview && (
                <div className="flex flex-wrap gap-2 items-end p-3 rounded-sm bg-muted/20">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Room Number</label>
                    <input
                      type="text"
                      value={scheduleRoomNumber}
                      onChange={(e) => setScheduleRoomNumber(e.target.value)}
                      placeholder="e.g. 2110"
                      className="px-3 py-2 text-sm rounded-sm border border-border bg-background w-32"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Start Date</label>
                    <input
                      type="date"
                      value={scheduleStart}
                      onChange={(e) => setScheduleStart(e.target.value)}
                      className="px-3 py-2 text-sm rounded-sm border border-border bg-background"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">End Date</label>
                    <input
                      type="date"
                      value={scheduleEnd}
                      onChange={(e) => setScheduleEnd(e.target.value)}
                      className="px-3 py-2 text-sm rounded-sm border border-border bg-background"
                    />
                  </div>
                  <Button onClick={handleCreateSchedule} disabled={creatingSchedule}>
                    {creatingSchedule ? "Scheduling..." : "Set Schedule"}
                  </Button>
                </div>
              )}

              {scheduleLoading ? (
                <p className="text-sm text-muted-foreground text-center py-6">Loading...</p>
              ) : schedules.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No RPM schedules yet</p>
              ) : (
                <div className="space-y-2">
                  {schedules.map((s) => (
                    <div key={s.id} className="flex items-center justify-between p-3 rounded-sm border border-border">
                      <div>
                        <p className="text-sm font-medium">Room {s.room_number}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(s.start_date).toLocaleDateString("en-GB")} – {new Date(s.end_date).toLocaleDateString("en-GB")} · set by {s.created_by_name || "-"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={s.used ? "bg-zinc-500/10 text-zinc-400 border-zinc-500/20 text-[10px]" : "bg-emerald-500/10 text-emerald-500 border-emerald-500/20 text-[10px]"}>
                          {s.used ? "Used" : "Active"}
                        </Badge>
                        {isHkReview && !s.used && (
                          <Button size="sm" variant="outline" onClick={() => handleCancelSchedule(s.id)}>Cancel</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === "approvals" && canApprove && (
          <Card className="orbit-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-sm uppercase tracking-wider">
                Approval Queue ({pendingApprovals.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingApprovals.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No submissions waiting for approval</p>
              ) : (
                <div className="space-y-2">
                  {pendingApprovals.map((sub) => {
                    const isExpanded = expandedApprovalId === sub.id;
                    const perluPerbaikanItems = sub.checklist_items.filter((i) => i.status === "PERLU_PERBAIKAN");
                    return (
                      <div key={sub.id} className="rounded-sm border border-border">
                        <div
                          className="flex items-center justify-between gap-3 p-3 cursor-pointer"
                          onClick={() => setExpandedApprovalId(isExpanded ? null : sub.id)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium">Room {sub.room_number}</p>
                              {sub.checklist_type === "VERIFICATION" ? (
                                <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[9px]">HK Verification</Badge>
                              ) : (
                                sub.is_repeat && (
                                  <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[9px]">Repeat PM</Badge>
                                )
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              by {sub.submitted_by_name || "-"} · {sub.ok_count} OK, {sub.perlu_perbaikan_count} needs repair · {isExpanded ? "hide details" : "click for details"}
                            </p>
                          </div>
                          <div className="flex gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" onClick={() => handleApprove(sub.id)} className="bg-green-600 hover:bg-green-700">Approve</Button>
                            <Button size="sm" variant="outline" onClick={() => handleReject(sub.id)}>Reject</Button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="border-t border-border p-3">
                            {perluPerbaikanItems.length === 0 ? (
                              <p className="text-xs text-muted-foreground">All items OK, no repairs needed.</p>
                            ) : (
                              <div className="space-y-1.5">
                                {perluPerbaikanItems.map((item, idx) => {
                                  const t = template.items.find((ti) => ti.id === item.id);
                                  return (
                                    <div key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                                      {item.foto && (
                                        <img
                                          src={`${process.env.REACT_APP_BACKEND_URL}${item.foto}`}
                                          alt="defect evidence"
                                          className="w-10 h-10 object-cover rounded border border-border shrink-0 cursor-pointer hover:opacity-80"
                                          onClick={() => setSelectedImage(`${process.env.REACT_APP_BACKEND_URL}${item.foto}`)}
                                        />
                                      )}
                                      <span>
                                        • <span className="text-foreground">{t?.description || `Item ${item.id}`}</span>
                                        {item.catatan ? ` — ${item.catatan}` : ""}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={!!historyRoom} onOpenChange={(open) => !open && setHistoryRoom(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wider">
              Room {historyRoom} - PM History
            </DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <p className="text-sm text-muted-foreground text-center py-6">Loading...</p>
          ) : historyData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No PM history for this room</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {historyData.map((sub) => {
                const isExpanded = expandedHistoryId === sub.id;
                const perluPerbaikanItems = sub.checklist_items.filter((i) => i.status === "PERLU_PERBAIKAN");
                const canExpand = perluPerbaikanItems.length > 0;
                return (
                  <div key={sub.id} className="border border-border rounded-sm p-3">
                    <div
                      className={canExpand ? "cursor-pointer" : ""}
                      onClick={() => canExpand && setExpandedHistoryId(isExpanded ? null : sub.id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium flex items-center gap-2">
                          {new Date(sub.submitted_at).toLocaleDateString("en-GB")}
                          {sub.checklist_type === "VERIFICATION" && (
                            <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[9px]">HK Verification</Badge>
                          )}
                        </span>
                        <Badge className="text-[10px]">{sub.approval_status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        by {sub.submitted_by_name || "-"} - {sub.ok_count} OK, {sub.perlu_perbaikan_count} needs repair
                        {canExpand && <> · {isExpanded ? "hide details" : "click for details"}</>}
                      </p>
                      {sub.reject_reason && (
                        <p className="text-xs text-red-400 mt-1">Reason: {sub.reject_reason}</p>
                      )}
                    </div>
                    {isExpanded && (
                      <div className="border-t border-border mt-2 pt-2 space-y-1.5">
                        {perluPerbaikanItems.map((item, idx) => {
                          const t = template.items.find((ti) => ti.id === item.id);
                          const badge = liveStatusBadge(item.live_status);
                          return (
                            <div key={idx} className="text-xs text-muted-foreground flex items-start gap-2 flex-wrap">
                              {item.foto && (
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}${item.foto}`}
                                  alt="defect evidence"
                                  className="w-10 h-10 object-cover rounded border border-border shrink-0 cursor-pointer hover:opacity-80"
                                  onClick={() => setSelectedImage(`${process.env.REACT_APP_BACKEND_URL}${item.foto}`)}
                                />
                              )}
                              <span>
                                • <span className="text-foreground">{t?.description || `Item ${item.id}`}</span>
                                {item.catatan ? ` — ${item.catatan}` : ""}
                              </span>
                              {badge && (
                                <Badge className={`text-[9px] shrink-0 ${badge.className}`}>{badge.label}</Badge>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <Button
              variant="ghost"
              size="sm"
              className="absolute -top-10 right-0 text-white hover:text-white hover:bg-white/20"
              onClick={() => setSelectedImage(null)}
            >
              <X size={20} />
            </Button>
            <img
              src={selectedImage}
              alt="Full size"
              className="max-w-full max-h-[85vh] object-contain rounded"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </Layout>
  );
};

export default RoomPM;
